import React, {useCallback, useEffect, useRef, useState} from "react";
import {ColumnFilterProps} from "../../../components/paginated-selector/filters/types";
import {useDropdownPosition} from "../../../hooks/useDropdownPosition";
import {ButtonV2} from "../../../components/ui/ButtonsV2/ButtonV2";

type StatusValue = "all" | "not_sent" | "queued" | "sent" | "failed" | "exempt" | "revoked";

const options: {value: StatusValue; label: string}[] = [
    {value: "all",      label: "All"},
    {value: "not_sent", label: "Not Sent"},
    {value: "queued",   label: "Queued"},
    {value: "sent",     label: "Sent"},
    {value: "failed",   label: "Failed"},
    {value: "exempt",   label: "Exempt"},
    {value: "revoked",  label: "Revoked"},
];

function valueFromParams(filterParams: ColumnFilterProps["filterParams"]): StatusValue {
    const s = filterParams.status;
    if (s === "not_sent" || s === "queued" || s === "sent" || s === "failed" || s === "exempt" || s === "revoked") return s;
    return "all";
}

export const OptInStatusFilter: React.FC<ColumnFilterProps> = ({label, onChange, filterParams}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const position = useDropdownPosition(isOpen, dropdownRef, 260);

    const [applied, setApplied]   = useState<StatusValue>(() => valueFromParams(filterParams));
    const [selected, setSelected] = useState<StatusValue>(() => valueFromParams(filterParams));

    useEffect(() => {
        const v = valueFromParams(filterParams);
        setApplied(v);
        setSelected(v);
    }, [filterParams.status]);

    const handleToggle = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        if (!isOpen) setSelected(applied);
        setIsOpen(o => !o);
    }, [isOpen, applied]);

    const handleApply = useCallback(() => {
        setApplied(selected);
        onChange({status: selected === "all" ? undefined : selected});
        setIsOpen(false);
    }, [selected, onChange]);

    const handleClear = useCallback(() => {
        setSelected("all");
        setApplied("all");
        onChange({status: undefined});
        setIsOpen(false);
    }, [onChange]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        if (isOpen) document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    const appliedOption = options.find(o => o.value === applied)!;

    return (
        <div className="relative inline-block" ref={dropdownRef}>
            <button
                onClick={handleToggle}
                className="flex items-center gap-1 hover:text-blue-600 transition-colors font-bold"
            >
                <span>{label}</span>
                {applied !== "all" && (
                    <span className="text-xs text-blue-600 font-normal">({appliedOption.label})</span>
                )}
                <span className={`text-xs transition-transform ${isOpen ? "rotate-180" : ""}`}>▼</span>
            </button>

            {isOpen && (
                <div
                    className={`absolute ${position} mt-1 bg-white rounded shadow-lg z-50 w-[260px] flex flex-col`}
                    onClick={e => e.stopPropagation()}
                >
                    <div>
                        {options.map(opt => (
                            <button
                                key={opt.value}
                                onClick={() => setSelected(opt.value)}
                                className="w-full text-left px-4 py-2 hover:bg-gray-100 font-normal flex items-center gap-2"
                            >
                                <input type="radio" readOnly checked={selected === opt.value} className="cursor-pointer"/>
                                <span>{opt.label}</span>
                            </button>
                        ))}
                    </div>
                    <div className="p-3 flex justify-center gap-2">
                        <div className="flex-1">
                            <ButtonV2 label="Clear" color="gray" onClick={handleClear}/>
                        </div>
                        <div className="flex-1">
                            <ButtonV2 label="Apply" color="blue" onClick={handleApply}/>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
